import React, { useEffect, useRef, useMemo } from "react";
import ApexCharts from "apexcharts";
import {PercentBadgeIcon} from "@heroicons/react/24/outline";

const PerformanceChart = ({
  title = "Response Speed Breakdown",
  fast = 0,
  moderate = 0,
  slow = 0,
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // 🔥 Calculate totals + percentages
  const { total, data } = useMemo(() => {
    const total = fast + moderate + slow;

    const calc = (val) =>
      total === 0 ? 0 : Math.round((val / total) * 100);

    return {
      total,
      data: [
        {
          label: "Fast",
          value: calc(fast),
          count: fast,
          color: "#22c55e", // green
        },
        {
          label: "Moderate",
          value: calc(moderate),
          count: moderate,
          color: "#f59e0b", // amber
        },
        {
          label: "Slow",
          value: calc(slow),
          count: slow,
          color: "#ef4444", // red
        },
      ],
    };
  }, [fast, moderate, slow]);

  // 🔥 Chart
  useEffect(() => {
    if (!chartRef.current) return;

    const options = {
      chart: {
        height: 350,
        width: 350,
        type: "radialBar",
      },
      series: data.map((d) => d.value),
      plotOptions: {
        radialBar: {
          hollow: {
            size: "45%",
          },
          track: {
            background: "#e5e7eb",
            opacity: 0.25,
          },
          dataLabels: {
            show: false,
          },
        },
      },
      stroke: {
        lineCap: "round",
      },
      colors: data.map((d) => d.color),
      legend: { show: false },
      tooltip: { enabled: false },
    };

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    chartInstance.current = new ApexCharts(chartRef.current, options);
    chartInstance.current.render();

    return () => chartInstance.current?.destroy();
  }, [data]);

    return (
        <div className="flex overflow-hidden flex-col justify-between h-full shadow-sm w-full">
          
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="flex items-center justify-center">
                  <PercentBadgeIcon className={`size-12 text-green-600`}/>
                </div>
        
                <div className="flex-1">
                  <h2 className="font-medium text-sm">
                    {title}
                  </h2>
        
                  <p className="text-xs text-gray-500 -mt-0.5 italic">
                    {total} total responses
                  </p>
                </div>
              </div>
            
            {/* Chart */}
            <div className="min-h-[280px] flex items-center justify-center">
              <div ref={chartRef} />
            </div>
    
            {/* Legend */}
            <div className="flex flex-row gap-5">
              {data.map((item, index) => (
                <div key={index} className="flex gap-2">
                  <span
                    className="size-2.5 rounded-full mt-2"
                    style={{ backgroundColor: item.color }}
                  />
                  <div className="flex-1">
                    <div>
                      <span className="font-semibold text-sm ">
                        {item.value}%
                      </span>
                      <span className="text-sm text-gray-800 dark:text-white">
                        {" "} - {item.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {item.count} responses
                    </p>
                  </div>
                </div>
              ))}
            </div>
    
        </div>
    );
};

export default PerformanceChart;
